import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SharedModule } from '../shared.module';
import { AwayOption } from '../away-status/away-status.component';
import { TMSService } from '../../core/service/tms/tms.service';
import { TmsSocketService } from '../../core/service/tms-socket/tms-socket.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { LoaderService } from '../../core/service/loader.service';
import { LockDialogService } from '../../core/service/lock-dialog/lock-dialog.service';
import { AuthenticationService } from '../../core/service/auth/authentication.service';
import { CoreSocketService } from '../../core/service/core-socket/core-socket.service';
import { MailCacheService } from '../../core/service/mailCache/mail-cache.service';
import { ProfileService } from '../../core/service/profile/profile.service';
import { SignOffChoiceComponent } from '../sign-off-choice/sign-off-choice.component';
import { TMS_AWAY_OPTIONS, TMS_INACTIVITY_OPTION, TMS_MANUAL_AWAY_OPTION, TMS_NO_REASON_REQUIRED_KEYS } from '../../core/constants/tms.const';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-away-lock',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './away-lock.component.html',
  styleUrl: './away-lock.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AwayLockComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private tmsService = inject(TMSService);
  private tmsSocketService = inject(TmsSocketService);
  private localStorageService = inject(LocalStorageService);
  private loader = inject(LoaderService);
  private _fb = inject(FormBuilder);
  // Own overlay container so lock-flow dialogs (wrong-code error, Logout confirmation)
  // render above the lock screen without touching — or closing — any popup the user
  // already had open before going idle.
  private lockDialog = inject(LockDialogService);
  private auth = inject(AuthenticationService);
  private socketService = inject(CoreSocketService);
  private mailCacheService = inject(MailCacheService);
  private profileService = inject(ProfileService);

  private _destroy$ = new Subject<void>();
  private static readonly _LOGOUT_KEY = 'tms_logout_signal';

  awayOption: AwayOption | null = null;

  // ── Locked-user identity (shown alongside the timer during inactivity locks) ──
  userName: string | null = null;
  userProfilePic = './assets/images/favicon_crizac.png';
  userSeatNo: string | null = null;
  userMobile: string | null = null;

  form: FormGroup = this._fb.group({
    digit1: ['', [Validators.required, Validators.maxLength(1)]],
    digit2: ['', [Validators.required, Validators.maxLength(1)]],
    digit3: ['', [Validators.required, Validators.maxLength(1)]],
    digit4: ['', [Validators.required, Validators.maxLength(1)]],
  });

  get quickPasswordCode(): string {
    return Object.values(this.form.value).join('');
  }

  // True for both system-triggered idle locks and manually-triggered away
  // locks — neither knows the specific break type at lock time anymore, so
  // both defer to the reason grid on resume.
  get isInactivityLock(): boolean {
    return this.awayOption?.key === TMS_INACTIVITY_OPTION.key
      || this.awayOption?.key === TMS_MANUAL_AWAY_OPTION.key;
  }

  // Every step lives on this same card/screen (instead of popups, which rendered
  // behind this screen's own overlay stack) — 'mpin' is the default entry point.
  step: 'mpin' | 'reason' | 'otherReason' | 'twoFactor' | 'resetMpin' = 'mpin';

  // ── Inactivity reason picker ───────────────────────────
  private static readonly _OTHER_OPTION_KEY = 'other';
  readonly reasonOptions: AwayOption[] = [
    ...TMS_AWAY_OPTIONS,
    { key: AwayLockComponent._OTHER_OPTION_KEY, label: 'Other', icon: 'edit_note', color: '#6B7280' },
  ];
  otherReasonControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3), Validators.maxLength(500)],
  });

  // Break-type-specific heading/placeholder for the free-text "otherReason"
  // step — steers the user toward what's actually useful to log for that
  // type (e.g. what was discussed in a meeting) instead of one generic prompt.
  private static readonly _REASON_PROMPTS: Record<string, { heading: string; placeholder: string }> = {
    phoneCall: {
      heading: 'Tell us about the call',
      placeholder: 'Who did you speak with, and what was discussed?',
    },
    meeting: {
      heading: 'Tell us about the meeting',
      placeholder: 'What was the agenda, and what did you discuss?',
    },
    workshop: {
      heading: 'Tell us about the workshop / training',
      placeholder: 'What did the session cover?',
    },
    brainstorming: {
      heading: 'Tell us about the brainstorming session',
      placeholder: 'What were you brainstorming, and what came out of it?',
    },
    clientVisit: {
      heading: 'Tell us about the client visit',
      placeholder: 'Which client did you visit, and what was the purpose?',
    },
    academicDataAndQuality: {
      heading: 'Tell us about the work',
      placeholder: 'What did you work on?',
    },
    [AwayLockComponent._OTHER_OPTION_KEY]: {
      heading: 'Tell us the reason for your break',
      placeholder: 'Type your reason here...',
    },
  };

  get otherReasonPrompt(): { heading: string; placeholder: string } {
    const key = this._pendingReasonOption?.key ?? AwayLockComponent._OTHER_OPTION_KEY;
    return AwayLockComponent._REASON_PROMPTS[key] ?? AwayLockComponent._REASON_PROMPTS[AwayLockComponent._OTHER_OPTION_KEY];
  }

  // ── Forgot MPIN: 2FA verification ──────────────────────
  twoFactorDigits: string[] = ['', '', '', '', '', ''];
  twoFactorControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^\d{6}$/)] });

  // ── Forgot MPIN: set new MPIN ───────────────────────────
  newMpinDigits: string[] = ['', '', '', ''];
  newMpinControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^\d{4}$/)] });

  // Timer
  elapsedSeconds = 0;
  private _timerInterval: any;
  private _breakStartTime = 0;
  private _visibilityListener: (() => void) | null = null;

  get formattedTime(): string {
    const h = Math.floor(this.elapsedSeconds / 3600);
    const m = Math.floor((this.elapsedSeconds % 3600) / 60);
    const s = this.elapsedSeconds % 60;
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(' : ');
  }

  ngOnInit(): void {
    // This overlay is a fixed <div> layered on top of the current page (not
    // routed — see app.component.ts), so the underlying page's own body
    // scrollbar stays active/visible unless suppressed here.
    document.body.style.overflow = 'hidden';

    // Reconnect TMS socket on refresh (away-lock has no header/layout)
    this.tmsSocketService.connect();
    this._loadUserInfo();

    // Read away option from router state (same tab navigation)
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as { awayOption: AwayOption } | undefined;

    if (state?.awayOption) {
      this.awayOption = state.awayOption;
    } else {
      // Fallback: history state (page refresh) then localStorage (new tab)
      const historyState = history.state as { awayOption: AwayOption } | undefined;
      this.awayOption = historyState?.awayOption ?? this.tmsSocketService.awayOption ?? null;
    }

    // Start timer — always derived from the shared break start time (not accumulated),
    // so it self-corrects for throttled/paused background tabs (browsers slow or fully
    // suspend setInterval in hidden tabs) and stays identical across every open tab.
    this._breakStartTime = this.tmsSocketService.breakStartTime ?? Date.now();
    this._updateElapsed();
    this._timerInterval = setInterval(() => this._updateElapsed(), 1000);

    this._visibilityListener = () => {
      if (!document.hidden) this._updateElapsed();
    };
    document.addEventListener('visibilitychange', this._visibilityListener);

    // Listen for server-side tms:unlock
    this.tmsSocketService.unlockEvent$
      .pipe(takeUntil(this._destroy$))
      .subscribe(unlocked => {
        if (unlocked) {
          clearInterval(this._timerInterval);
          this.tmsSocketService.hideLockOverlay();
        }
      });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    clearInterval(this._timerInterval);
    if (this._visibilityListener) {
      document.removeEventListener('visibilitychange', this._visibilityListener);
      this._visibilityListener = null;
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _updateElapsed(): void {
    this.elapsedSeconds = Math.floor((Date.now() - this._breakStartTime) / 1000);
  }

  private async _loadUserInfo(): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    this.userName = currentUser?.name ?? null;
    this.userProfilePic = currentUser?.profilepic
      ? `${environment.fileBaseUrl}${currentUser.profilepic}`
      : './assets/images/favicon_crizac.png';
    this.userSeatNo = currentUser?.employeeSeatNo ?? null;
    this.userMobile = currentUser?.phone ?? null;
  }

  onInput(event: any, nextInputId: string): void {
    this.lockErrorMessage = null;
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length > 1) {
      input.value = value.slice(0, 1);
      this.form.get(input.getAttribute('formControlName')!)?.setValue(input.value);
    }

    if (value !== '' && nextInputId) {
      const nextInput = document.getElementById(nextInputId) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');

    // Only allow digits
    const cleanText = pastedText.replace(/\D/g, '').substring(0, 4);

    if (cleanText) {
      // Array of our form control names in order
      const digits = ['digit1', 'digit2', 'digit3', 'digit4'];

      for (let i = 0; i < cleanText.length && i < digits.length; i++) {
        // Update form value
        this.form.get(digits[i])?.setValue(cleanText[i]);

        // Update DOM input value manually to avoid sync issues.
        const inputElement = document.getElementById(digits[i]) as HTMLInputElement;
        if (inputElement) {
          inputElement.value = cleanText[i];
        }
      }

      // Focus on the next empty input, or the last input if full
      const nextFocusIndex = Math.min(cleanText.length, digits.length - 1);
      const nextInputId = digits[nextFocusIndex];
      const nextInputToFocus = document.getElementById(nextInputId) as HTMLInputElement;
      if (nextInputToFocus) {
        nextInputToFocus.focus();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, prevInputId: string, nextInputId: string): void {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      if (input.value === '' && prevInputId) {
        const prevInput = document.getElementById(prevInputId) as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
        }
      }
    }

    if (event.key === 'ArrowLeft' && prevInputId) {
      event.preventDefault();
      const prevInput = document.getElementById(prevInputId) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }

    if (event.key === 'ArrowRight' && nextInputId) {
      event.preventDefault();
      const nextInput = document.getElementById(nextInputId) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Prevent non-numeric input
    if (!/^[0-9]$/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Delete' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  isUnlocking = false;

  // Shown inline on the card (instead of a dialog, which rendered behind this
  // screen's own overlay stack) — cleared at the start of the next action.
  lockErrorMessage: string | null = null;

  private _showLockError(message: string): void {
    this.lockErrorMessage = message;
  }

  onUnlock(): void {
    if (this.form.invalid || this.isUnlocking) return;

    // Inactivity locks require picking a reason before the code is verified —
    // revealed inline on this same card rather than a popup (which rendered
    // behind this screen's own overlay stack).
    if (this.isInactivityLock && this.step === 'mpin') {
      this.step = 'reason';
      return;
    }

    // Not an inactivity lock — the break type was already fixed when the user
    // picked it from the away-status grid, so just echo it back on end.
    this._completeUnlock(this.awayOption?.key);
  }

  // Break type the "otherReason" free-text step is currently collecting a
  // reason for — set by selectReason, read by submitOtherReason.
  private _pendingReasonOption: AwayOption | null = null;

  selectReason(option: AwayOption): void {
    if (TMS_NO_REASON_REQUIRED_KEYS.includes(option.key)) {
      this._completeUnlock(option.key, option.label);
      return;
    }
    this._pendingReasonOption = option;
    this.step = 'otherReason';
  }

  backToReasonGrid(): void {
    this.step = 'reason';
    this._pendingReasonOption = null;
    this.otherReasonControl.reset('');
    this.lockErrorMessage = null;
  }

  submitOtherReason(): void {
    if (this.otherReasonControl.invalid || this.isUnlocking) {
      this.otherReasonControl.markAsTouched();
      return;
    }
    const breakType = this._pendingReasonOption?.key ?? AwayLockComponent._OTHER_OPTION_KEY;
    this._completeUnlock(breakType, this.otherReasonControl.value.trim());
  }

  private async _completeUnlock(breakType?: string, reason?: string): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');

    this.isUnlocking = true;
    this.lockErrorMessage = null;
    this.loader.show();
    this.tmsService.breakEnd(currentUser?.id, this.quickPasswordCode, breakType, reason).subscribe({
      next: () => {
        this.isUnlocking = false;
        this.loader.hide();
        this.tmsSocketService.markUnlocked();
        clearInterval(this._timerInterval);
        this.tmsSocketService.hideLockOverlay();
      },
      error: (err: any) => {
        this.isUnlocking = false;
        this.loader.hide();
        // Send back to the MPIN step — the reason grid has no field to surface a
        // wrong-code error against, and the code may be what actually failed.
        this.step = 'mpin';
        this.form.reset();
        const msg = err?.error?.message || 'Invalid code. Please try again.';
        this._showLockError(msg);
      },
    });
  }

  async logout(): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');

    if (currentUser?.email === 'automation@crizac.com') {
      this._doLogout();
      return;
    }

    // Bypass the lock screen for the duration of this dialog instead of fighting
    // its z-index — the lock screen is only re-shown below if the user cancels.
    this.tmsSocketService.hideLockOverlay();

    const dialogRef = this.lockDialog.open(SignOffChoiceComponent, {
      panelClass: 'custom-dialog-container',
      width: '480px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((choice: 'end_day' | 'temporary' | undefined) => {
      if (choice === 'end_day') {
        // This overlay sits above the router (shown via tmsSocketService.overlayVisible$,
        // not a route), so navigating alone leaves it covering the wrap-up page underneath.
        // Deliberately NOT calling markUnlocked() here — the user hasn't actually signed
        // off yet, just chosen to start that flow. The TMS lock stays in effect (so a
        // refresh or a browser-back out of the wrap-up page re-locks instead of dropping
        // the user straight back into the app) until SignOffWrapupComponent confirms the
        // sign-off actually completed.
        clearInterval(this._timerInterval);
        this.tmsSocketService.hideLockOverlay();
        this.router.navigate(['/sign-off-wrapup'], { state: { fromLock: true } });
      } else if (choice === 'temporary') {
        this._doLogout();
      } else {
        // Cancelled — the user is still meant to be locked, so restore the screen.
        this.tmsSocketService.showLockOverlay();
      }
    });
  }

  private _doLogout(): void {
    this.auth.logout().subscribe({
      next: async (res: any) => {
        if (res?.data?.status) {
          await this._finishLogout();
        } else {
          this._showLockError('Invalid credentials');
        }
      },
      // Logout requests skip the auth interceptor (SKIP_AUTH — see
      // HttpClientService.post's noHeaders branch), so a session that already
      // expired server-side surfaces here as a plain request error instead of
      // the app-wide session-expire dialog. Since the whole point of this
      // button is to leave the locked session, still finish logging out
      // locally rather than trapping the user behind the lock screen with a
      // "Login failed" message that has no login form to retry against.
      error: async () => {
        await this._finishLogout();
      },
    });
  }

  private async _finishLogout(): Promise<void> {
    // Signal other tabs to logout — must be before disconnect clears localStorage
    localStorage.setItem(AwayLockComponent._LOGOUT_KEY, 'true');
    this.socketService.disconnect();
    this.tmsSocketService.disconnect();
    this.tmsService.reset();
    this.localStorageService.removeIndexedDbKey('currentUser');
    this.localStorageService.clearIndexedDb();
    this.localStorageService.removeIndexedDbKey('switchUser');
    this.localStorageService.removeKey('access_token');
    this.localStorageService.removeKey('refreshToken');
    this.localStorageService.removeKey('switchUserToken');
    this.localStorageService.removeKey('isSwitchUser');
    this.localStorageService.removeKey('twoFactorVerified');
    this.localStorageService.removeKey('roles_permissions');
    this.localStorageService.removeKey('notification_banner_shown');
    this.localStorageService.removeKey('currentUserMailDetails');
    await this.mailCacheService.clearAllCache();
    clearInterval(this._timerInterval);
    this.router.navigate(['/login']);
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  forgotMpin(): void {
    this.step = 'twoFactor';
    this.lockErrorMessage = null;
  }

  // Cancels the Forgot-MPIN flow from either of its steps, back to the MPIN entry.
  cancelForgotFlow(): void {
    this.step = 'mpin';
    this.twoFactorDigits = ['', '', '', '', '', ''];
    this.twoFactorControl.reset('');
    this.newMpinDigits = ['', '', '', ''];
    this.newMpinControl.reset('');
    this.lockErrorMessage = null;
  }

  onTwoFactorDigitInput(event: Event, index: number, nextInput: HTMLInputElement | null): void {
    this.lockErrorMessage = null;
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    input.value = digit;
    this.twoFactorDigits[index] = digit;
    this.twoFactorControl.setValue(this.twoFactorDigits.join(''));
    this.twoFactorControl.markAsTouched();

    if (digit && nextInput) {
      nextInput.focus();
    }
  }

  onTwoFactorDigitKeydown(event: KeyboardEvent, index: number, prevInput: HTMLInputElement | null): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevInput) {
      prevInput.focus();
    }
  }

  verifyTwoFactorCode(): void {
    if (this.twoFactorControl.invalid || this.isUnlocking) {
      this.twoFactorControl.markAsTouched();
      return;
    }

    this.isUnlocking = true;
    this.lockErrorMessage = null;
    this.loader.show();
    this.profileService.verifyTwoFactorCode(this.twoFactorControl.value).subscribe({
      next: () => {
        this.isUnlocking = false;
        this.loader.hide();
        this.step = 'resetMpin';
      },
      error: (err: any) => {
        this.isUnlocking = false;
        this.loader.hide();
        const msg = err?.error?.message || 'Verification failed. Please enter a valid code.';
        this._showLockError(msg);
      },
    });
  }

  onNewMpinDigitInput(event: Event, index: number, nextInput: HTMLInputElement | null): void {
    this.lockErrorMessage = null;
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    input.value = digit;
    this.newMpinDigits[index] = digit;
    this.newMpinControl.setValue(this.newMpinDigits.join(''));
    this.newMpinControl.markAsTouched();

    if (digit && nextInput) {
      nextInput.focus();
    }
  }

  onNewMpinDigitKeydown(event: KeyboardEvent, index: number, prevInput: HTMLInputElement | null): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevInput) {
      prevInput.focus();
    }
  }

  async saveNewMpin(): Promise<void> {
    if (this.newMpinControl.invalid || this.isUnlocking) {
      this.newMpinControl.markAsTouched();
      return;
    }

    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    if (!currentUser?.id) return;

    this.isUnlocking = true;
    this.lockErrorMessage = null;
    this.loader.show();
    this.profileService.updateUserDetails(currentUser.id, {
      userCredentials: { mpin: this.newMpinControl.value }
    }).subscribe({
      next: async (res: any) => {
        this.isUnlocking = false;
        this.loader.hide();
        if (res.statusCode === 200) {
          currentUser.isMpinAdded = true;
          await this.localStorageService.setIndexedDbKey('currentUser', currentUser);
          this.cancelForgotFlow();
        } else {
          this._showLockError(res?.message || 'Failed to reset MPIN');
        }
      },
      error: (err: any) => {
        this.isUnlocking = false;
        this.loader.hide();
        const msg = err?.error?.message || 'Failed to reset MPIN. Please try again.';
        this._showLockError(msg);
      },
    });
  }
}